import {
  ComponentType,
  lazy,
  Suspense,
  useEffect,
} from "react";
import {
  createBrowserRouter,
  Outlet,
  redirect,
  RouterProvider,
} from "react-router-dom";
import { AdminLayout } from "./admin-layout";
import "./App.css";
import { Footer } from "./components/footer/footer.component";
import { Navbar } from "./components/navbar/navbar.component";
import { AppLoader } from "./components/appLoader/appLoader";
import { CountrySelectionProvider } from "./components/countrySelector/country-selection.context";
import ErrorFallback from "./components/errorFallback";
import { AppRoutes } from "./models";
import { setUser } from "./store/authSlice";
import store from "./store/store";
import { Roles, User } from "./types";

function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => {
    const module = await loader();
    return { default: module[exportName] as ComponentType };
  });
}

const Homepage = lazyNamed(
  () => import("./pages/homepage/homepage.page"),
  "Homepage",
);
const JobSearchResult = lazyNamed(
  () => import("./pages/jobSearchResult/jobSearchResult.page"),
  "JobSearchResult",
);
const JobDescription = lazyNamed(
  () => import("./pages/jobDescription/jobDescription.page"),
  "JobDescription",
);
const RedirectExternalPage = lazyNamed(
  () => import("./pages/redirectExternalPage/redirectExternal.page"),
  "RedirectExternalPage",
);
const LoginPage = lazyNamed(
  () => import("./pages/login/login.page"),
  "LoginPage",
);
const SignUpPage = lazyNamed(
  () => import("./pages/signup/signup.page"),
  "SignUpPage",
);
const ForgetPasswordPage = lazyNamed(
  () => import("./pages/forget-password/forget-password.page"),
  "ForgetPasswordPage",
);
const UserDashboardPage = lazyNamed(
  () => import("./pages/dashboard/user-dashboard.page"),
  "UserDashboardPage",
);
const RssFeedPage = lazyNamed(
  () => import("./pages/rss-feed/rss-feed.page"),
  "RssFeedPage",
);
const ContactUsPage = lazyNamed(
  () => import("./pages/contact-us/contact-us.page"),
  "ContactUsPage",
);
const PrivacyPolicyPage = lazyNamed(
  () => import("./pages/privacy-policy/privacy-policy.page"),
  "PrivacyPolicyPage",
);
const ImprintPage = lazyNamed(
  () => import("./pages/imprint/imprint.page"),
  "ImprintPage",
);
const GdprPage = lazyNamed(
  () => import("./pages/gdpr/gdpr.page"),
  "GdprPage",
);
const AlertUnsubscribePage = lazyNamed(
  () => import("./pages/alerts/unsubscribe/alert-unsubscribe.page"),
  "AlertUnsubscribePage",
);
const JobAlertPage = lazyNamed(
  () => import("./pages/jobAlert/jobAlert.page"),
  "JobAlertPage",
);
const AdminDashboard = lazyNamed(
  () => import("./pages/dashboard/admin-dashboard.page"),
  "AdminDashboard",
);

const RootLayout = () => {
  return (
    <>
      <Navbar />
      <main>
        <Suspense fallback={<AppLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </>
  );
};

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: (
      <>
        <Navbar />
        <ErrorFallback />
        <Footer />
      </>
    ),
    children: [
      {
        path: AppRoutes.HOME,
        element: <Homepage />,
      },
      {
        path: AppRoutes.JOB_SEARCH,
        element: <JobSearchResult />,
      },
      {
        path: `${AppRoutes.JOB}/:cc/:title/:sr`,
        element: <JobDescription />,
      },
      {
        path: `${AppRoutes.COMPLETE_APPLICATION}`,
        element: <RedirectExternalPage />,
      },
      {
        path: `${AppRoutes.LOGIN}`,
        element: <LoginPage />,
      },
      {
        path: `${AppRoutes.SIGNUP}`,
        element: <SignUpPage />,
      },
      {
        path: `${AppRoutes.FORGET_PASSWORD}`,
        element: <ForgetPasswordPage />,
      },
      {
        path: `${AppRoutes.DASHBOARD}`,
        element: <UserDashboardPage />,
      },
      {
        path: AppRoutes.RSS,
        element: <RssFeedPage />,
      },
      {
        path: AppRoutes.CONTACT_US,
        element: <ContactUsPage />,
      },
      {
        path: AppRoutes.TERMS_AND_PRIVACY,
        element: <PrivacyPolicyPage />,
      },
      {
        path: AppRoutes.IMPRINT,
        element: <ImprintPage />,
      },
      {
        path: AppRoutes.GDPR,
        element: <GdprPage />,
      },
      {
        path: `${AppRoutes.ALERT_UNSUBSCRIBE}/:id`,
        element: <AlertUnsubscribePage />,
      },
      {
        path: AppRoutes.JOB_ALERT,
        element: <JobAlertPage />,
      },
    ],
  },
  {
    path: `${AppRoutes.ADMIN_DASHBOARD}/:tab?`,
    element: (
      <AdminLayout>
        <Suspense fallback={<AppLoader />}>
          <AdminDashboard />
        </Suspense>
      </AdminLayout>
    ),
    errorElement: (
      <AdminLayout>
        <h2>Something went wrong in the admin section.</h2>
        <p>Please try refreshing the page.</p>
      </AdminLayout>
    ),
    loader: () => {
      const user: User | null = sessionStorage.getItem("user")
        ? JSON.parse(sessionStorage.getItem("user") as string)
        : null;

      if (!user || user.role.name !== Roles.ADMIN) {
        return redirect(AppRoutes.HOME);
      }

      return null;
    },
  },
]);

function App() {
  useEffect(() => {
    store.dispatch(setUser(readStoredUser()));
  }, []);

  return (
    <CountrySelectionProvider>
      <RouterProvider router={router} />
    </CountrySelectionProvider>
  );
}

function readStoredUser(): User | null {
  try {
    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) {
      return null;
    }
    return JSON.parse(storedUser) as User;
  } catch (error: unknown) {
    console.warn("Ignoring invalid user session data", error);
    return null;
  }
}

export default App;
